import React, { useState, useEffect, useCallback } from "react";
import { getDisplaySettings, updateDisplaySettings } from "../../Libs/api";
import Loader from "../../Component/Loader";
import { Monitor, Trophy, Settings, Presentation } from "lucide-react";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import useBreakPoints from "../../hooks/useBreakPoints";

const ManageDisplay = () => {
  const { isMobile } = useBreakPoints();
  const [displayScreen, setDisplayScreen] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    showScoreDisplay: false,
    showAds: false,
    showLeaderboard: false,
  });
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const settingsResponse = await getDisplaySettings(displayScreen);

      if (settingsResponse.success) {
        setSettings({
          showScoreDisplay: settingsResponse.data.showScoreDisplay || false,
          showAds: settingsResponse.data.showAds || false,
          showLeaderboard: settingsResponse.data.showLeaderboard || false,
        });
      } else {
        setError("Failed to fetch display settings");
      }
    } catch (error) {
      setError("Error fetching data: " + error.message);
    } finally {
      setLoading(false);
    }
  }, [displayScreen]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSwitchChange = async (switchName) => {
    try {
      setSaving(true);
      setError("");

      // Create new settings object with only the selected switch as true
      const newSettings = {
        showScoreDisplay: switchName === "showScoreDisplay",
        showAds: switchName === "showAds",
        showLeaderboard: switchName === "showLeaderboard",
      };

      const response = await updateDisplaySettings(newSettings, displayScreen);

      if (response.success) {
        setSettings(newSettings);
        toast.success("Display settings updated successfully");
      } else {
        Swal.fire(
          "Error!",
          response.message || "Failed to update settings",
          "error"
        );
      }
    } catch (error) {
      Swal.fire("Error!", "Error updating settings: " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <div
        style={{
          minHeight: "100vh",
          padding: "0",
          margin: "0",
          color: "white",
          paddingBottom: 80,
          overflowY: "auto",
          maxHeight: "100vh",
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: "#001837",
            color: "white",
            padding: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: 20,
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "900",
                color: "white",
                margin: "0 0 8px 0",
              }}
            >
              Display Management
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#E5E7EB",
                margin: "0",
              }}
            >
              Configure each venue screen independently
            </p>
          </div>
        </div>

        {/* Display 1 / Display 2 — segmented control */}
        <div
          style={{
            padding: "12px 16px 16px",
            backgroundColor: "#001837",
          }}
        >
          <div
            role="tablist"
            aria-label="Choose which venue display to configure"
            style={{
              display: "flex",
              maxWidth: 420,
              padding: 4,
              borderRadius: 14,
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.2)",
              gap: 4,
            }}
          >
            {[1, 2].map((n) => {
              const active = displayScreen === n;
              return (
                <button
                  key={n}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setDisplayScreen(n)}
                  disabled={loading || saving}
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    padding: "12px 14px",
                    borderRadius: 11,
                    border: "none",
                    cursor: loading || saving ? "not-allowed" : "pointer",
                    opacity: loading || saving ? 0.65 : 1,
                    transition:
                      "background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
                    backgroundColor: active ? "#ffffff" : "transparent",
                    color: active ? "#001837" : "rgba(255, 255, 255, 0.88)",
                    fontWeight: 700,
                    fontSize: "15px",
                    letterSpacing: "0.02em",
                    boxShadow: active
                      ? "0 4px 14px rgba(0, 0, 0, 0.22), 0 1px 0 rgba(255,255,255,0.9) inset"
                      : "none",
                  }}
                >
                  <Presentation
                    size={18}
                    strokeWidth={active ? 2.25 : 2}
                    style={{
                      flexShrink: 0,
                      opacity: active ? 1 : 0.85,
                    }}
                  />
                  <span style={{ whiteSpace: "nowrap" }}>
                    Display&nbsp;{n}
                  </span>
                </button>
              );
            })}
          </div>
          <p
            style={{
              margin: "10px 0 0",
              fontSize: 12,
              color: "rgba(255, 255, 255, 0.55)",
              fontWeight: 500,
            }}
          >
            Editing controls for the screen opened at{" "}
            <code
              style={{
                color: "rgba(186, 230, 253, 0.95)",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              /display-{displayScreen}
            </code>
          </p>
        </div>

        {/* Settings Container */}
        <div
          style={{
            backgroundColor: "#f8fafc",
            minHeight: "calc(100vh - 200px)",
            padding: "20px 16px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              boxShadow:
                "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            }}
          >
            {/* Settings Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "24px",
                paddingBottom: "16px",
                borderBottom: "2px solid #f1f5f9",
              }}
            >
              <div
                style={{
                  backgroundColor: "#001837",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Settings size={20} color="white" />
              </div>
              <div>
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#1e293b",
                    margin: "0 0 4px 0",
                  }}
                >
                  Display Settings
                </h3>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#64748b",
                    margin: "0",
                  }}
                >
                  Choose what content to show on this screen
                </p>
              </div>
            </div>

            {/* Switch Options */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              {/* Score Display Switch */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px",
                  backgroundColor: settings.showScoreDisplay
                    ? "#f0f9ff"
                    : "#f8f9fa",
                  borderRadius: "8px",
                  border: settings.showScoreDisplay
                    ? "2px solid #0ea5e9"
                    : "2px solid #e5e7eb",
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      backgroundColor: settings.showScoreDisplay
                        ? "#0ea5e9"
                        : "#6b7280",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Monitor size={16} color="white" />
                  </div>
                  <div>
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#1e293b",
                        margin: "0 0 4px 0",
                      }}
                    >
                      Show Score Display
                    </h4>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        margin: "0",
                      }}
                    >
                      Display live match scores and game information
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleSwitchChange("showScoreDisplay")}
                  disabled={saving}
                  style={{
                    width: isMobile ? "62px" : "48px",
                    height: "24px",
                    borderRadius: "12px",
                    border: "none",
                    backgroundColor: settings.showScoreDisplay
                      ? "#0ea5e9"
                      : "#d1d5db",
                    cursor: saving ? "not-allowed" : "pointer",
                    position: "relative",
                    transition: "all 0.2s ease-in-out",
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "white",
                      position: "absolute",
                      top: "2px",
                      left: settings.showScoreDisplay ? "26px" : "2px",
                      transition: "all 0.2s ease-in-out",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                </button>
              </div>

              {/* Ads Switch */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px",
                  backgroundColor: settings.showAds ? "#f0f9ff" : "#f8f9fa",
                  borderRadius: "8px",
                  border: settings.showAds
                    ? "2px solid #0ea5e9"
                    : "2px solid #e5e7eb",
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      backgroundColor: settings.showAds ? "#0ea5e9" : "#6b7280",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Monitor size={16} color="white" />
                  </div>
                  <div>
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#1e293b",
                        margin: "0 0 4px 0",
                      }}
                    >
                      Show Ads
                    </h4>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        margin: "0",
                      }}
                    >
                      Display promotional content and advertisements
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleSwitchChange("showAds")}
                  disabled={saving}
                  style={{
                    width: isMobile ? "62px" : "48px",
                    height: "24px",
                    borderRadius: "12px",
                    border: "none",
                    backgroundColor: settings.showAds ? "#0ea5e9" : "#d1d5db",
                    cursor: saving ? "not-allowed" : "pointer",
                    position: "relative",
                    transition: "all 0.2s ease-in-out",
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "white",
                      position: "absolute",
                      top: "2px",
                      left: settings.showAds ? "26px" : "2px",
                      transition: "all 0.2s ease-in-out",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                </button>
              </div>
              {/* Leaderboard Switch */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px",
                  backgroundColor: settings.showLeaderboard
                    ? "#f0f9ff"
                    : "#f8f9fa",
                  borderRadius: "8px",
                  border: settings.showLeaderboard
                    ? "2px solid #0ea5e9"
                    : "2px solid #e5e7eb",
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      backgroundColor: settings.showLeaderboard
                        ? "#0ea5e9"
                        : "#6b7280",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Trophy size={16} color="white" />
                  </div>
                  <div>
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#1e293b",
                        margin: "0 0 4px 0",
                      }}
                    >
                      Show Leaderboard
                    </h4>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        margin: "0",
                      }}
                    >
                      Display team rankings and player statistics
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleSwitchChange("showLeaderboard")}
                  disabled={saving}
                  style={{
                    width: isMobile ? "62px" : "48px",
                    height: "24px",
                    borderRadius: "12px",
                    border: "none",
                    backgroundColor: settings.showLeaderboard
                      ? "#0ea5e9"
                      : "#d1d5db",
                    cursor: saving ? "not-allowed" : "pointer",
                    position: "relative",
                    transition: "all 0.2s ease-in-out",
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "white",
                      position: "absolute",
                      top: "2px",
                      left: settings.showLeaderboard ? "26px" : "2px",
                      transition: "all 0.2s ease-in-out",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                </button>
              </div>
            </div>

            {/* )} */}

            {/* Current Status */}
            <div
              style={{
                marginTop: "24px",
                padding: "16px",
                backgroundColor: "#f0f9ff",
                borderRadius: "8px",
                border: "1px solid #bae6fd",
              }}
            >
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#0369a1",
                  margin: "0 0 8px 0",
                }}
              >
                Current Display Status
              </h4>
              <p
                style={{
                  fontSize: "14px",
                  color: "#0369a1",
                  margin: "0",
                }}
              >
                {settings.showScoreDisplay && "📊 Score Display is active"}
                {settings.showAds && "📢 Ads are active"}
                {settings.showLeaderboard && "🏆 Leaderboard is active"}
                {!settings.showScoreDisplay &&
                  !settings.showAds &&
                  !settings.showLeaderboard &&
                  "⚪ No display option is currently active"}
              </p>
            </div>

            {/* Error Display */}
            {error && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  backgroundColor: "#fef2f2",
                  borderRadius: "8px",
                  border: "1px solid #fecaca",
                }}
              >
                <p
                  style={{
                    fontSize: "14px",
                    color: "#dc2626",
                    margin: "0",
                  }}
                >
                  {error}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageDisplay;
